import client from '@config/client'
import API_URL from '@config/services'
import {
  ApiClientResult,
  ApiClientResponse,
  OrgUser,
  OrgUserPayload,
  Team,
  TeamPayload,
  TeamMemberPayload,
  TeamUsersPayload
} from '@/types/api'
import { ID } from '../types'

interface InvitePayload {
  email: string
  first_name?: string
  last_name?: string
  role?: string
  [key: string]: any
}

interface TeamUserPayload {
  team_id?: number | string
  orguser_id?: number | string
  user_ids?: (number | string)[]
  [key: string]: any
}

interface UpdateMemberPayload {
  role?: string
  title?: string
  is_active?: boolean
  [key: string]: any
}

interface CreateUserPayload {
  email: string
  first_name?: string
  last_name?: string
  [key: string]: any
}

/**
 * Gets all organization user profiles
 * @param payload - Optional query parameters
 * @returns List of organization users
 */
const onGetAllProfiles = (payload?: any): Promise<ApiClientResult> =>
  client
    .get(API_URL.ORG_USER.GET_ALL_ORG_USERS(), payload)
    .then(response => response)
    .catch(error => error.response)

/**
 * Invites a new member to a team
 * @param orgId - Organization ID
 * @param teamId - Team ID
 * @param payload - Member data
 * @returns API response
 */
export const onInviteMember = (
  orgId: number | string,
  teamId: number | string,
  payload: InvitePayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.ORG_USER.GET_ORG_USER_PK(orgId) + `?team=${teamId}`, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Updates an existing team member
 * @param userId - User ID
 * @param payload - Updated member data
 * @param headers - Optional request headers
 * @returns Updated user data
 */
export const onUpdateMember = (
  userId: number | string,
  payload: UpdateMemberPayload,
  headers: Record<string, string> = {}
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.ORG_USER.UPDATE_ORG_USER(userId), payload, headers)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Deletes a team member
 * @param id - User ID to delete
 * @returns API response
 */
export const onDeleteMember = (
  id: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.ORG_USER.DELETE_ORG_USER(id))
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Creates a new team
 * @param orgId - Organization ID
 * @param payload - Team data
 * @returns Created team
 */
export const onCreateTeam = (
  orgId: number | string,
  payload: TeamPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.TEAMS.POST_TEAMS_LIST(orgId), payload)
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Updates team information
 * @param teamId - Team ID
 * @param payload - Updated team data
 * @returns Updated team
 */
export const onUpdateTeamInfo = (
  teamId: number | string,
  payload: Partial<TeamPayload>
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.TEAMS.PATCH_TEAM(teamId), payload)
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Adds a user to a team
 * @param orgId - Organization ID
 * @param payload - Team member data
 * @returns API response
 */
export const addUserToTeam = (
  orgId: number | string,
  payload: TeamUserPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.TEAMS.ADD_ORGUSER_TO_TEAM(orgId), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Updates team users in bulk
 * @param id - Team ID
 * @param payload - Team users action payload
 * @returns API response
 */
export const patchTeamsUsers = (
  id: number | string,
  payload: TeamUserPayload
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.TEAMS.PATCH_TEAM_USERS(id), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Removes a user from a team
 * @param orgId - Organization ID
 * @param payload - Team member data to remove
 * @returns API response
 */
export const removeUserFromTeam = (
  orgId: number | string,
  payload: TeamUserPayload
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.TEAMS.REMOVE_ORGUSER_FROM_TEAM(orgId), { data: payload })
    .then(response => response)
    .catch(error => error.response)
}

/* Orgusers */

/**
 * Creates a new organization user
 * @param orgId - Organization ID
 * @param payload - User data
 * @returns Created user
 */
export const createUser = (
  orgId: number | string,
  payload: CreateUserPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.ORG_USER.POST_ORGUSER_LIST(orgId), payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Gets a team by ID
 * @param teamPk - Team ID
 * @returns Team data
 */
export const getTeam = (teamPk: number | string): Promise<ApiClientResult> => {
  return client
    .get(API_URL.TEAMS.GET_TEAM(teamPk))
    .then(response => response)
    .catch(error => error.response)
}

export default onGetAllProfiles
